import { z } from 'zod';
import type { ContentItemType, ContentItemStatus } from '../types/content';

const contentItemSchema = z.object({
  title: z.string(),
  type: z.enum(['text', 'champions', 'gallery', 'press', 'data', 'media', 'committee', 'testimonials'] as const) as z.ZodType<ContentItemType>,
  content: z.string(),
  status: z.enum(['Published', 'Draft'] as const) as z.ZodType<ContentItemStatus>,
  section_id: z.string().optional(),
  lastUpdated: z.string().optional()
});

type ContentItemData = z.infer<typeof contentItemSchema>;

export const eventSchema = z.object({
  tournament_id: z.string(),
  title: z.string().min(1, { message: "Title is required" }),
  date: z.string().min(1, { message: "Date is required" }),
  time: z.string().min(1, { message: "Time is required" }),
  location: z.string().min(1, { message: "Location is required" }),
  type: z.enum(['Registration', 'Tournament', 'Ceremony', 'Social']),
  description: z.string().optional()
});

export type EventData = z.infer<typeof eventSchema>;

export const tournamentSchema = z.object({
  name: z.string().min(1, { message: "Tournament name is required" }),
  start_date: z.string().min(1, { message: "Start date is required" }),
  end_date: z.string().min(1, { message: "End date is required" }),
  status: z.enum(['upcoming', 'ongoing', 'completed', 'cancelled']),
  capacity: z.number().min(1, { message: "Capacity must be at least 1" }),
  registration_fee: z.number().min(0, { message: "Registration fee cannot be negative" }).max(999999999.99, { message: "Registration fee cannot exceed ₦999,999,999.99" }),
  prize_pool: z.number().min(0, { message: "Prize pool cannot be negative" }).max(999999999.99, { message: "Prize pool cannot exceed ₦999,999,999.99" }),
  description: z.string().optional(),
  venue: z.string().optional()
});

export type TournamentData = z.infer<typeof tournamentSchema>;

export const logoSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  url: z.string().url({ message: "Valid URL is required" }),
  type: z.enum(['primary', 'secondary', 'favicon']),
  location: z.enum(['header', 'footer']),
  format: z.enum(['svg', 'png', 'ico']),
  width: z.number().optional(),
  height: z.number().optional(),
  is_active: z.boolean().default(false)
});

export type LogoData = z.infer<typeof logoSchema>;

export const partnerFormSchema = z.object({
  companyName: z.string()
    .min(2, 'Company name must be at least 2 characters')
    .max(100, 'Company name must be less than 100 characters'),

  contactPerson: z.string()
    .min(2, 'Contact person name must be at least 2 characters')
    .max(100, 'Contact person name must be less than 100 characters'),

  email: z.string()
    .email('Please enter a valid email address'),

  phone: z.string()
    .min(10, 'Phone number must be at least 10 characters')
    .max(15, 'Phone number must be less than 15 characters')
    .regex(/^\+?[0-9]*$/, 'Please enter a valid phone number'),

  sponsorshipTier: z.enum([
    'Tournament Host',
    'Diamond',
    'Platinum',
    'Gold',
    'Silver',
    'Bronze',
    'Official Airline',
    'Official Bank',
    'Official Beverage',
    'Official Telecom Partner',
    'Official Automotive Brand',
    'Official Insurance Partner',
    'Official Real Estate Partner',
    'Official Media Partner',
    'Official Logistics Partner',
    'Official Construction Partner',
    'Official Apparel Brand'
  ], {
    required_error: 'Please select a sponsorship tier',
    invalid_type_error: 'Please select a valid sponsorship tier'
  }),

  categoryTierInterest: z.string().optional(),

  tournamentId: z.string().uuid({ message: "Valid tournament ID is required" }).optional()
});

export type PartnerFormData = z.infer<typeof partnerFormSchema>;

export const registrationSchema = z.object({
  firstName: z.string().min(2, { message: "First name must be at least 2 characters" }),
  lastName: z.string().min(2, { message: "Last name must be at least 2 characters" }),
  sex: z.enum(['male', 'female']),
  category: z.string().min(1, { message: "Please select a category" }),
  residentClub: z.string().min(2, { message: "Resident club must be at least 2 characters" }),
  handicap: z.string().refine((val) => {
    const num = parseInt(val);
    return !isNaN(num) && num >= 0 && num <= 36;
  }, { message: "Handicap must be between 0 and 36" }),
  phone: z.string().min(10, { message: "Phone number must be at least 10 characters" }),
  email: z.string().email({ message: "Invalid email address" }),
  shirtSize: z.enum(['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'], {
    required_error: "Shirt size is required",
    invalid_type_error: "Please select a valid shirt size"
  }),
  tournamentId: z.string().uuid({ message: "Valid tournament ID is required" }).optional(),
  bankName: z.string().min(2, { message: "Bank name must be at least 2 characters" }).optional(),
  accountNumber: z.string().min(10, { message: "Account number must be at least 10 characters" }).optional(),
  transferDate: z.string().min(1, { message: "Transfer date is required" }).optional(),
  transferAmount: z.string().min(1, { message: "Transfer amount is required" }).optional(),
  senderName: z.string().min(2, { message: "Sender name must be at least 2 characters" }).optional(),
  proofOfPayment: z.any().refine((file) => file instanceof File, { message: "Proof of payment is required" })
});

export type RegistrationFormData = z.infer<typeof registrationSchema>;

export type PlayerData = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  country: string;
  resident_club: string;
  handicap: number;
  category: string;
  tournament_id?: string;
  registration_status: {
    personalInfo: boolean;
    payment: boolean;
    verification: boolean;
  };
  status: string;
  payment_proof_url?: string;
  shirt_size?: string;
  registration_number?: string;
  gender?: string;
};

export type PartnerData = {
  company_name: string;
  contact_person: string;
  email: string;
  phone: string;
  tier: string;
  status: string;
  contribution: number;
  start_date: string;
  end_date: string;
  benefits: string[];
  tournament_id?: string;
  description?: string;
  logo_url?: string;
  website?: string;
  image_url?: string;
  stats?: string[];
};

// Financial transaction schema
const financialTransactionSchema = z.object({
  tournament_id: z.string().uuid({ message: "Valid tournament ID is required" }),
  type: z.enum(['income', 'expense'], { 
    required_error: "Transaction type is required",
    invalid_type_error: "Transaction type must be either 'income' or 'expense'"
  }),
  category: z.string().min(1, { message: "Category is required" }),
  description: z.string().min(1, { message: "Description is required" }),
  amount: z.number().positive({ message: "Amount must be greater than 0" }),
  transaction_date: z.string().min(1, { message: "Transaction date is required" }),
  status: z.enum(['pending', 'completed', 'cancelled'], {
    required_error: "Status is required",
    invalid_type_error: "Status must be 'pending', 'completed', or 'cancelled'"
  }),
  payment_method: z.string().optional(),
  reference_number: z.string().optional(),
  notes: z.string().optional(),
  partner_id: z.string().uuid({ message: "Valid partner ID is required" }).optional(),
  player_id: z.string().uuid({ message: "Valid player ID is required" }).optional(),
  created_by: z.string().uuid({ message: "Valid user ID is required" }).optional()
});

export type FinancialTransactionData = z.infer<typeof financialTransactionSchema>;

// Budget item schema
const budgetItemSchema = z.object({
  tournament_id: z.string().uuid({ message: "Valid tournament ID is required" }),
  category: z.string().min(1, { message: "Category is required" }),
  subcategory: z.string().optional(),
  description: z.string().min(1, { message: "Description is required" }),
  allocated_amount: z.number().nonnegative({ message: "Allocated amount cannot be negative" }),
  spent_amount: z.number().nonnegative({ message: "Spent amount cannot be negative" }).optional(),
  fiscal_year: z.number().int().positive().optional(),
  status: z.enum(['active', 'closed', 'archived'], {
    required_error: "Status is required",
    invalid_type_error: "Status must be 'active', 'closed', or 'archived'"
  }),
  created_by: z.string().uuid({ message: "Valid user ID is required" }).optional()
});

export type BudgetItemData = z.infer<typeof budgetItemSchema>;

// Budget adjustment schema
const budgetAdjustmentSchema = z.object({
  budget_item_id: z.string().uuid({ message: "Valid budget item ID is required" }),
  previous_amount: z.number().nonnegative({ message: "Previous amount cannot be negative" }),
  new_amount: z.number().nonnegative({ message: "New amount cannot be negative" }),
  adjustment_date: z.string().min(1, { message: "Adjustment date is required" }),
  reason: z.string().min(1, { message: "Reason is required" }),
  approved_by: z.string().uuid({ message: "Valid user ID is required" }).optional(),
  created_by: z.string().uuid({ message: "Valid user ID is required" }).optional()
});

export type BudgetAdjustmentData = z.infer<typeof budgetAdjustmentSchema>;

// Player score schema
export const playerScoreSchema = z.object({
  player_id: z.string().uuid(),
  tournament_id: z.string().uuid(),
  round: z.number().int().positive(),
  score: z.number().int(),
  date: z.string().min(1, { message: "Date is required" }),
  notes: z.string().optional()
});

export type PlayerScoreData = z.infer<typeof playerScoreSchema>;

// CSV import schema
export const csvImportSchema = z.object({
  player_id: z.string().uuid({ message: "Player ID must be a valid UUID" }),
  round: z.number().int().positive({ message: "Round must be a positive integer" }),
  score: z.number().int({ message: "Score must be an integer" }),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: "Date must be in YYYY-MM-DD format" }),
  notes: z.string().optional()
});

export type CsvImportData = z.infer<typeof csvImportSchema>;

// Function to generate registration numbers for players without one
export async function generateRegistrationNumbers() {
  return Promise.resolve();
}

// Prospect pipeline schemas
export const PROSPECT_STAGES = ['lead','contacted','proposal_sent','negotiation','committed','won','lost'] as const;
export type ProspectStage = typeof PROSPECT_STAGES[number];

export const STAGE_LABELS: Record<ProspectStage, string> = {
  lead: 'Lead',
  contacted: 'Contacted',
  proposal_sent: 'Proposal Sent',
  negotiation: 'Negotiation',
  committed: 'Committed',
  won: 'Won',
  lost: 'Lost',
};

export const STAGE_CONFIG: Record<ProspectStage, { label: string; color: string; headerBg: string; border: string; dot: string }> = {
  lead:          { label: 'Lead',          color: 'bg-gray-100 text-gray-600',      headerBg: 'bg-gray-50',     border: 'border-gray-300',    dot: 'bg-gray-400' },
  contacted:     { label: 'Contacted',     color: 'bg-blue-50 text-blue-700',       headerBg: 'bg-blue-50',     border: 'border-blue-300',    dot: 'bg-blue-500' },
  proposal_sent: { label: 'Proposal Sent', color: 'bg-yellow-50 text-yellow-700',   headerBg: 'bg-yellow-50',   border: 'border-yellow-300',  dot: 'bg-yellow-500' },
  negotiation:   { label: 'Negotiation',   color: 'bg-orange-50 text-orange-700',   headerBg: 'bg-orange-50',   border: 'border-orange-300',  dot: 'bg-orange-500' },
  committed:     { label: 'Committed',     color: 'bg-emerald-50 text-emerald-700', headerBg: 'bg-emerald-50',  border: 'border-emerald-300', dot: 'bg-emerald-500' },
  won:           { label: 'Won',           color: 'bg-green-100 text-green-800',    headerBg: 'bg-green-50',    border: 'border-green-400',   dot: 'bg-green-600' },
  lost:          { label: 'Lost',          color: 'bg-red-50 text-red-600',         headerBg: 'bg-red-50',      border: 'border-red-300',     dot: 'bg-red-400' },
};

export const prospectFormSchema = z.object({
  company_name:   z.string().min(2, 'Company name must be at least 2 characters'),
  industry:       z.string().optional(),
  company_size:   z.enum(['startup','sme','mid-market','enterprise','unknown']).optional(),
  website:        z.string().url('Enter a valid URL').optional().or(z.literal('')),
  stage:          z.enum(PROSPECT_STAGES).default('lead'),
  source:         z.enum(['referral','cold_outreach','inbound','event','social','other']).optional(),
  source_detail:  z.string().optional(),
  notes:          z.string().optional(),
  lost_reason:    z.string().optional(),
});
export type ProspectFormData = z.infer<typeof prospectFormSchema>;

export const prospectContactSchema = z.object({
  full_name:                z.string().min(2, 'Name is required'),
  title:                    z.string().optional(),
  email:                    z.string().email('Valid email required').optional().or(z.literal('')),
  phone:                    z.string().optional(),
  preferred_contact_method: z.enum(['email','phone','whatsapp','linkedin','in_person']).optional(),
  linkedin_url:             z.string().url('Valid URL required').optional().or(z.literal('')),
  is_primary:               z.boolean().default(false),
  is_official_contact:      z.boolean().default(false),
  street:                   z.string().optional(),
  city:                     z.string().optional(),
  state:                    z.string().optional(),
  postal_code:              z.string().optional(),
  country:                  z.string().optional(),
  notes:                    z.string().optional(),
});
export type ProspectContactData = z.infer<typeof prospectContactSchema>;

export const prospectTaskSchema = z.object({
  task_type:  z.enum(['call','email','meeting','follow_up','proposal','demo','other']),
  title:      z.string().min(2, 'Title is required'),
  due_date:   z.string().optional(),
  priority:   z.enum(['low','medium','high','urgent']).default('medium'),
  notes:      z.string().optional(),
});
export type ProspectTaskData = z.infer<typeof prospectTaskSchema>;

export const prospectCommitmentSchema = z.object({
  tier_interest:   z.string().optional(),
  amount_pledged:  z.number().nonnegative().optional(),
  expected_close:  z.string().optional(),
  probability_pct: z.number().min(0).max(100).optional(),
  status:          z.enum(['draft','verbal','written','signed','cancelled']).default('draft'),
  notes:           z.string().optional(),
});
export type ProspectCommitmentData = z.infer<typeof prospectCommitmentSchema>;

export const prospectActivateSchema = z.object({
  tier:           z.string().min(1, 'Tier is required'),
  contribution:   z.number().positive('Contribution must be positive'),
  start_date:     z.string().min(1, 'Start date is required'),
  end_date:       z.string().min(1, 'End date is required'),
  contact_person: z.string().min(2, 'Contact person is required'),
  email:          z.string().email('Valid email required'),
  phone:          z.string().min(10, 'Valid phone number required'),
  benefits:       z.array(z.string()).default([]),
  notes:          z.string().optional(),
});
export type ProspectActivateData = z.infer<typeof prospectActivateSchema>;